import React from 'react';

import TextButton from 'CommonComponents/TextButton';

const STATUS_CREATED = 'created';
const STATUS_SENT_FOR_APPROVAL = 'sent_for_approval';
const STATUS_FAILED = 'failed';

/**
 * What the last submission did.
 *
 * Created and sent-for-approval rows are listed individually — each is a
 * commitment someone may need to go and find — but failures are aggregated one
 * line per reason, the shape BulkApplyResultBar uses: a submission of hundreds
 * can fail for the same cause hundreds of times, and "12 have no supplier" is
 * the useful form of that.
 */
function ResultSection({ title, tone, children }) {
    return (
        <div className="tw-flex tw-flex-col tw-gap-y-1">
            <div className={`tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide ${tone}`}>{title}</div>
            {children}
        </div>
    );
}

export default function SendForCommitmentResults({ results, suppliers, onDismiss }) {
    const created = results.filter((row) => row.status === STATUS_CREATED);
    const sent = results.filter((row) => row.status === STATUS_SENT_FOR_APPROVAL);
    const failed = results.filter((row) => row.status === STATUS_FAILED);

    const failedByReason = failed.reduce((carry, row) => {
        // A failure the server did not explain still has to be counted, and
        // saying so is better than dropping the row out of the tally.
        const reason = row.reason || $t('No reason given');
        carry[reason] = (carry[reason] ?? 0) + 1;

        return carry;
    }, {});

    const nameFor = (row) => suppliers[row.allocation_id] || $t('Unknown supplier');

    /**
     * The supplier name, linked to the order it raised.
     *
     * A new tab, for the same reason Edit budget line opens one: this summary sits
     * over a queue still holding unsaved selections, splits and cashflows for every
     * row that did NOT commit, and navigating away in this tab would discard them.
     *
     * Falls back to plain text rather than a dead link — `url` is absent on a
     * failed row, which never had an order to point at.
     */
    const LinkedName = ({ row }) =>
        row.url ? (
            <a href={row.url} target="_blank" rel="noopener noreferrer" className="tw-text-charcoal tw-underline">
                {nameFor(row)}
            </a>
        ) : (
            <span className="tw-text-charcoal">{nameFor(row)}</span>
        );

    return (
        <div className="table__card tw-mb-4" role="status">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-x-4 tw-px-4 tw-py-3">
                <div className="tw-flex tw-flex-col tw-gap-y-4 tw-text-sm">
                    {created.length > 0 && (
                        <ResultSection title={$t('{{count}} committed', { count: created.length })} tone="tw-text-green">
                            {created.map((row) => (
                                <div key={row.allocation_id} className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2">
                                    <LinkedName row={row} />
                                    {/* Only a row committed outright has a code: PurchaseOrder::generateCode()
                                        fires at approval, so a pending one has none to show. */}
                                    {!!row.reference && <span className="tw-font-mono tw-text-xs tw-text-charcoal-75">{row.reference}</span>}
                                </div>
                            ))}
                        </ResultSection>
                    )}

                    {sent.length > 0 && (
                        <ResultSection title={$t('{{count}} sent for approval', { count: sent.length })} tone="tw-text-blue">
                            {sent.map((row) => (
                                <div key={row.allocation_id}>
                                    <LinkedName row={row} />
                                </div>
                            ))}
                            <div className="tw-text-xs tw-text-charcoal-50">
                                {$t('These stay in the queue until their commitment is approved. No reference exists until then.')}
                            </div>
                        </ResultSection>
                    )}

                    {failed.length > 0 && (
                        <ResultSection title={$t('{{count}} could not be committed', { count: failed.length })} tone="tw-text-red">
                            {Object.entries(failedByReason).map(([reason, count]) => (
                                <div key={reason} className="tw-text-charcoal">
                                    {$t('{{count}} × {{reason}}', { count, reason })}
                                </div>
                            ))}
                        </ResultSection>
                    )}
                </div>

                <TextButton onClick={onDismiss}>{$t('Dismiss')}</TextButton>
            </div>
        </div>
    );
}
